import React, { FC, useEffect, useState, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Pressable,
  Checkbox,
  Button,
} from "native-base";
import { Loc } from "../../App/Loc";
import { CrossIcon } from "../../assets/icons/icon-assets";
import AVInputtext from "../../components/AVInputtext";
import AVDatePicker from "../../components/common/AVDatePicker";
import dayjs, { Dayjs } from "dayjs";
import * as AV from "../../AVCore/AVCore";
import { Dimensions } from "react-native";
import { useNavigate } from "react-router-dom";
import CustomToast from "../../Hooks/useCustomToast";
import * as AVI from "../../AVCore/Interfaces/AV.Interfaces";
import { RootStateOrAny, useSelector, useDispatch } from "react-redux";
import { setEntitydata, setUser } from "../../Redux/Actions/userActions";

interface Props {
  entity: AV.IAVEntity;
  showCreateFeeCollection: boolean;
  setShowCreateFeeCollection: React.Dispatch<React.SetStateAction<boolean>>;
}

const AVCreateFeeCollection: FC<Props> = ({
  entity,
  showCreateFeeCollection,
  setShowCreateFeeCollection,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dimensions = Dimensions.get("window");
  const isScreen = dimensions?.width <= 1000;
  const isMobile = dimensions?.width <= 600;

  const currentUser = useSelector(
    (state: RootStateOrAny) => state.user.user[0],
  );
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );

  const [title, setTitle] = useState("");
  const [fee, setFee] = useState("");
  const [members, setMembers] = useState(false);
  const [players, setPlayers] = useState(false);
  const [description, setDescription] = useState("");

  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [isDateError, setIsDateError] = useState(false);

  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAPICall, setIsAPICall] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const [profile, setProfile] = useState<AVI.IProfile>({
    background: undefined,
    pic: undefined,
    text: undefined,
    color: undefined,
  });

  type CheckboxConfig = {
    key: "member" | "player";
    label: string;
  };

  const getCheckboxConfig = (entity): CheckboxConfig[] => {
    const { type, subType } = entity || {};

    if (type === "Team" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
      ];
    }

    if (type === "PlayerOrg" && !subType) {
      return [
        {
          key: "player",
          label: Loc.currentLang.feeCollect.players,
        },
        {
          key: "member",
          label: Loc.currentLang.menu.parents,
        },
      ];
    }

    if (type === "School" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.menu.staff,
        },
      ];
    }

    if (type === "SchoolYear" && !subType) {
      return [
        {
          key: "player",
          label: Loc.currentLang.menu.students,
        },
        {
          key: "member",
          label: Loc.currentLang.menu.parents,
        },
      ];
    }

    if (type === "PlayerOrg" && subType == "Term") {
      return [
        {
          key: "player",
          label: Loc.currentLang.feeCollect.members,
        },
        {
          key: "member",
          label: Loc.currentLang.menu.parents,
        },
      ];
    }

    if (type === "Team" && subType == "Club") {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
      ];
    }

    if (type === "Club" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
      ];
    }

    if (type === "Group" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
      ];
    }

    if (type === "PlayerOrg" && subType == "Class") {
      return [
        {
          key: "player",
          label: Loc.currentLang.menu.students,
        },
        {
          key: "member",
          label: Loc.currentLang.menu.parents,
        },
      ];
    }

    if (type === "PlayerOrg" && subType == "GradClass") {
      return [
        {
          key: "player",
          label: Loc.currentLang.menu.students,
        },
        {
          key: "member",
          label: Loc.currentLang.menu.parents,
        },
      ];
    }

    if (type === "Organization" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.menu.employee,
        },
      ];
    }

    if (type === "Neighborhood" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.menu.neighbors,
        },
      ];
    }

    if (type === "Community" && !subType) {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
      ];
    }

    if (type === "PlayerOrg" && subType == "MusicGroup") {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
        {
          key: "player",
          label: Loc.currentLang.feeCollect.players,
        },
      ];
    }

    if (type === "Group" && subType == "Department") {
      return [
        {
          key: "member",
          label: Loc.currentLang.feeCollect.members,
        },
      ];
    }

    if (type === "Event" || type === "Game") {
      return [
        {
          key: "member",
          label: Loc.currentLang.menu.participants,
        },
      ];
    }

    // DEFAULT / FUTURE CASES
    return [
      {
        key: "member",
        label: Loc.currentLang.feeCollect.members,
      },
      {
        key: "player",
        label: Loc.currentLang.feeCollect.players,
      },
    ];
  };

  const checkboxConfig = getCheckboxConfig(entity);

  const validateDates = (start: Date | null, end: Date | null) => {
    const today = dayjs().startOf("day");

    if (!start || !end)
      return {
        isError: true,
        message: Loc.currentLang.feeCollect.requiredDates,
      };

    const startDay = dayjs(start).startOf("day");
    const endDay = dayjs(end).startOf("day");

    if (!startDay.isValid() || !endDay.isValid())
      return {
        isError: true,
        message: Loc.currentLang.feeCollect.invalidDate,
      };

    if (startDay.isBefore(today))
      return {
        isError: true,
        message: Loc.currentLang.feeCollect.startDatePast,
      };

    if (endDay.isBefore(today))
      return {
        isError: true,
        message: Loc.currentLang.feeCollect.endDatePast,
      };

    if (startDay.isAfter(endDay))
      return {
        isError: true,
        message: Loc.currentLang.feeCollect.startAfterEnd,
      };

    return { isError: false, message: "" };
  };

  const handleStartDate = (date: Dayjs | null) => {
    const newStart = date && date.isValid() ? date.toDate() : null;

    setStartDate(newStart);
    //setIsDateError(validateDates(newStart, endDate));
  };

  const handleEndDate = (date: Dayjs | null) => {
    const newEnd = date && date.isValid() ? date.toDate() : null;

    setEndDate(newEnd);
    //setIsDateError(validateDates(startDate, newEnd));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png"];

    if (!allowed.includes(file.type)) {
      setError(Loc.currentLang.feeCollect.invalidImageType);
      return;
    }

    setIsDateError(true);
    setError("");
    await uploadProfilePic(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const fileInput = imageInputRef.current as HTMLInputElement | null;
    if (!fileInput) return;

    setIsDateError(true);
    let files = Array.from(e.dataTransfer.files) as File[];
    const allowedExt = ["jpg", "jpeg", "png"];

    const validFiles = files.filter((file: File) => {
      let ext = file.name.split(".").pop()?.toLowerCase();
      return ext !== undefined && allowedExt.includes(ext);
    });

    if (validFiles.length === 0) {
      setIsDateError(false);
      setError(Loc.currentLang.feeCollect.invalidImageType);
      return;
    }

    fileInput.value = "";
    setError("");
    await uploadProfilePic(validFiles[0]);
  };

  const arrayToFileList = (files) => {
    const dt = new DataTransfer();
    files.forEach((file) => dt.items.add(file));
    return dt.files;
  };

  const uploadProfilePic = async (file) => {
    try {
      const fileList = arrayToFileList([file]);

      let media = await AV.Media.saveFiles(
        AV.Media.getFormData(fileList),
        entity?.region ? entity?.region : currentUser?.region,
      );

      if (!media || media.length === 0) {
        setIsDateError(false);
        setError(Loc.currentLang.feeCollect.uploadFailed);
        return;
      }

      setProfile((prev) => ({
        ...prev,
        background: media[0],
      }));
      setSelectedFileName(
        media ? (media[0]?.orgFile ?? "") : (fileList[0]?.name ?? ""),
      );
      setIsDateError(false);
    } catch (err) {
      setIsDateError(false);
      console.error("Upload failed", err);
      setError(Loc.currentLang.feeCollect.uploadFailed);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleCreate = async () => {
    setIsAPICall(true);

    try {
      // ---- VALIDATION ----
      if (!title.trim()) {
        let clientError = {
          message: Loc.currentLang.feeCollect.errorTitleRequired,
        };
        CustomToast({ clientError });
        setIsAPICall(false);
        return;
      }

      const result = validateDates(startDate, endDate);
      if (result.isError) {
        let clientError = { message: result.message };
        CustomToast({ clientError });
        setIsAPICall(false);
        return;
      }

      if (!fee || isNaN(Number(fee))) {
        let clientError = {
          message: Loc.currentLang.feeCollect.errorInvalidAmount,
        };
        CustomToast({ clientError });
        setIsAPICall(false);
        return;
      }

      if (!members && !players) {
        let clientError = {
          message: Loc.currentLang.feeCollect.errorSelectMembers,
        };
        CustomToast({ clientError });
        setIsAPICall(false);
        return;
      }

      // ---- OPTIONS ----
      const options: AVI.ICollectOptions = {
        collectReason: "F", // Fee Collection
        personAmount: Number(fee) || 0,
        //chargeFee: members || players, // true if either selected
        currency: "USD",
        collectType:
          entity?.type === "Event" || entity?.type === "Game"
            ? "Participants"
            : players && members
              ? "Both"
              : players
                ? "Players"
                : members
                  ? "Members"
                  : "None",
        //trackByPerson: true,
        //perPersonFee: true,
      };

      // ---- MAIN DATA ----
      type ExtendedCollect = AVI.ICollect & {
        preferences?: AV.IPreferences;
      };
      const data: ExtendedCollect = {
        type: "Collect",
        name: title,
        profile: profile,
        start: startDate ?? undefined,
        end: endDate ?? undefined,
        goal: Number(fee) || 0,
        options: options,
        //members: entity?.members,
        parent: AV.AVEntity.getLink(entity),
        preferences: {
          isPrivate: false,
        } as AV.IPreferences,
      };

      // ---- SAVE COLLECT ----
      console.log("collect data", data);
      const feeData = await AV.AVEntity.save(data);

      if (feeData) {
        let finalUser = await AV.Person.getSelf(true);
        dispatch(setUser(finalUser));

        let index = entityDataList?.findIndex((x) => x?.id === finalUser?.id);
        if (index !== -1) {
          entityDataList[index] = finalUser;
          dispatch(setEntitydata(entityDataList));
        }
      }

      navigate(`/teams/${feeData?.id}/${feeData?.region}`, {
        state: { isCreate: false, team: feeData, newEntity: true },
      });
      setShowCreateFeeCollection(false);
    } catch (err) {
      console.error("Create Collect Failed:", err);
    } finally {
      setIsAPICall(false);
      setShowCreateFeeCollection(false);
    }
  };

  const handleCancel = () => {
    setShowCreateFeeCollection(false);
  };

  return (
    <Box
      position="fixed"
      bgColor="rgba(0,0,0,0.25)"
      bottom={0}
      right={0}
      top={0}
      left={0}
      height="100%"
      width="100%"
    >
      <div
        style={{
          position: "fixed",
          top: "56%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "#ffffff",
          boxShadow:
            "0 12px 28px 0 rgba(0,0,0,0.2), 0 2px 4px 0 rgba(0,0,0,0.1)",
          width: isMobile ? "88%" : "580px",
          borderRadius: "8px",
          height: "88%",
          overflow: "auto",
        }}
      >
        <VStack width="100%" marginBottom="30px">
          {/* Header */}
          <HStack
            alignItems="center"
            width="auto"
            marginX="24px"
            marginTop="24px"
            justifyContent="space-between"
          >
            <Text fontSize={30}>
              {Loc.currentLang.feeCollect.createFeeCollection}
            </Text>

            <Pressable onPress={handleCancel}>
              <CrossIcon size="40px" color="#000" />
            </Pressable>
          </HStack>

          {/* Form Fields */}
          <VStack
            width="auto"
            marginX="24px"
            space={6}
            marginBottom="10px"
            top="25px"
          >
            {/* Title */}
            <Box width="100%">
              <AVInputtext
                type="text"
                label={Loc.currentLang.feeCollect.title}
                value={title}
                onChange={(e) => setTitle(e?.target?.value ?? e)}
                maxLength={50}
              />
            </Box>

            {/* Dates */}
            <HStack space="2%" flexWrap="wrap">
              <Box width={isScreen ? "100%" : "48.5%"}>
                <AVDatePicker
                  key={`end-date-picker-${refreshKey}`}
                  label={Loc.currentLang.feeCollect.startDate}
                  onChange={handleStartDate}
                  defaultValue={startDate ? dayjs(startDate) : null}
                  disable={false}
                />
              </Box>

              <Box width={isScreen ? "100%" : "48.5%"}>
                <AVDatePicker
                  key={`end-date-picker-${refreshKey}`}
                  label={Loc.currentLang.feeCollect.endDate}
                  onChange={handleEndDate}
                  defaultValue={endDate ? dayjs(endDate) : null}
                  disable={false}
                />
              </Box>
            </HStack>

            {/* Fee */}
            <Box width="100%">
              <AVInputtext
                type="text"
                label={Loc.currentLang.feeCollect.FeeDollar}
                value={fee}
                maxLength={7}
                onChange={(e) => {
                  let input = e?.target?.value ?? e;

                  // Allow numbers and only ONE decimal point
                  input = input
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1");

                  // Limit to 2 decimal places
                  const parts = input.split(".");
                  if (parts[1]?.length > 2) {
                    input = `${parts[0]}.${parts[1].slice(0, 2)}`;
                  }

                  setFee(input);
                }}
              />
            </Box>

            {/* Checkboxes */}
            <HStack space="35px" alignItems="center">
              {checkboxConfig.map((item) => (
                <Checkbox
                  key={item.key}
                  value={item.key}
                  isChecked={item.key === "member" ? members : players}
                  onChange={(value) =>
                    item.key === "member"
                      ? setMembers(value)
                      : setPlayers(value)
                  }
                >
                  {item.label}
                </Checkbox>
              ))}
            </HStack>

            {/* Description */}
            <Box width="100%">
              <AVInputtext
                type="text"
                minRows={1}
                multiline={true}
                value={description}
                maxLength={250}
                onChange={(e) => {
                  const value = e?.target?.value ?? e;
                  setProfile((p) => ({ ...p, text: value }));
                  setDescription(value);
                }}
                label={Loc.currentLang.feeCollect.description}
              />
            </Box>

            <VStack
              borderStyle={"dashed"}
              borderColor={"gray.300"}
              borderRadius={"5px"}
              borderWidth={"1px"}
              padding={"20px"}
              width={"100%"}
              alignSelf={"center"}
              zIndex={-3}
              marginTop={"10px"}
            >
              {/* Hidden File Input */}
              <input
                type="file"
                ref={imageInputRef}
                accept="image/png, image/jpeg"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />

              <div
                style={{ textAlign: "center" }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <HStack justifyContent={"center"} space={2}>
                  <Pressable onPress={() => imageInputRef?.current?.click()}>
                    <HStack space={2}>
                      <Text color={"#00A7BC"}>
                        {Loc.currentLang.feeCollect.uploadCoverImage}{" "}
                      </Text>
                      <Text color={"black"} fontWeight={500}>
                        {Loc.currentLang.menu.or}
                      </Text>
                      <Text color={"black"}>
                        {Loc.currentLang.menu.dragNDrop}
                      </Text>
                    </HStack>
                  </Pressable>
                </HStack>
                <Text textAlign={"center"} color={"gray.300"}>
                  ({"JPG, PNG"})
                </Text>

                {/* ERROR MESSAGE */}
                {error && (
                  <Text
                    textAlign={"center"}
                    color={"#DF2827"}
                    marginTop={"5px"}
                  >
                    {" "}
                    {error}
                  </Text>
                )}

                {/* FILE NAME DISPLAY */}
                {selectedFileName && (
                  <Text
                    textAlign="center"
                    color="black"
                    marginTop="8px"
                    fontWeight={500}
                  >
                    {" "}
                    {selectedFileName}
                  </Text>
                )}
              </div>
            </VStack>

            {/* Footer Buttons */}
            <HStack
              justifyContent="center"
              alignItems="center"
              width="100%"
              paddingY="10px"
              //marginTop="20px"
              space={4}
            >
              <Button
                width={isMobile || isScreen ? "40%" : "180px"}
                height="40px"
                backgroundColor="#EEEEEE"
                rounded="full"
                onPress={handleCancel}
              >
                <Text color="rgba(0,0,0,0.5)">
                  {Loc.currentLang.menu.cancel}
                </Text>
              </Button>

              <Button
                width={isMobile || isScreen ? "40%" : "180px"}
                height="40px"
                backgroundColor="#32AE78"
                rounded="full"
                isDisabled={
                  !title?.trim() || // title empty
                  !fee || // empty fee
                  Number(fee) <= 0 || // 0, 00, 000 etc.
                  isDateError || // invalid date range or uplading cover pic
                  (!members && !players)
                }
                isLoading={isAPICall}
                onPress={handleCreate}
              >
                <Text color="white">{Loc.currentLang.menu.create}</Text>
              </Button>
            </HStack>
          </VStack>
        </VStack>
      </div>
    </Box>
  );
};

export default AVCreateFeeCollection;
